import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  House, 
  GraduationCap, 
  Money, 
  EnvelopeSimple,
  CalendarCheck,
  Calendar,
  SignOut,
  List,
  X,
  Globe,
  ForkKnife,
  Bus
} from '@phosphor-icons/react';
import PushInboxBell from './PushInboxBell';

const ParentLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/parent', icon: House, label: t('dashboard') },
    { path: '/parent/timetable', icon: Calendar, label: t('timetable') },
    { path: '/parent/appointments', icon: CalendarCheck, label: t('appointments') },
    { path: '/parent/cantine', icon: ForkKnife, label: 'Cantine' },
    { path: '/parent/transport', icon: Bus, label: 'Transport' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E8F0] z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hover:bg-[#F1F5F9]"
        >
          {sidebarOpen ? <X size={24} /> : <List size={24} />}
        </Button>
        <span className="font-outfit font-bold text-[#10B981]">{t('parentPortal')}</span>
        <div className="flex items-center gap-1">
          <PushInboxBell compact />
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="hover:bg-[#F1F5F9]"
          >
            <Globe size={20} />
          </Button>
        </div>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E2E8F0] z-40 transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-center border-b border-[#E2E8F0] px-4 bg-gradient-to-r from-[#10B981] to-[#059669]">
            <GraduationCap size={32} className="text-white mr-2" weight="duotone" />
            <span className="font-outfit font-bold text-white text-lg">
              {t('parentPortal')}
            </span>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-[#E2E8F0]">
            <p className="font-manrope font-medium text-[#0F172A] truncate">{user?.name}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#10B981]/10 text-[#10B981]">
              Parent
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-manrope text-sm transition-all duration-200
                    ${isActive 
                      ? 'bg-[#10B981] text-white' 
                      : 'text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]'}`}
                >
                  <item.icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-[#E2E8F0] space-y-2">
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="w-full justify-start text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
            >
              <Globe size={20} className="mr-3" />
              {language === 'fr' ? 'English' : 'Français'}
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-[#EF4444] hover:text-[#EF4444] hover:bg-[#FEF2F2]"
            >
              <SignOut size={20} className="mr-3" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop-only floating bell (top-right) */}
        <div className="hidden lg:flex fixed top-4 right-6 z-30 items-center gap-1 bg-white border border-[#E2E8F0] rounded-full shadow-sm px-1 py-1">
          <PushInboxBell />
        </div>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ParentLayout;
